import React, { useEffect, useRef } from "react";

const TOOLBAR_ACTIONS = [
  { id: "bold", label: "Bold", command: "bold", iconSrc: "/assets/icon/bold.svg" },
  { id: "italic", label: "Italic", command: "italic", iconSrc: "/assets/icon/italics.svg" },
  {
    id: "quote",
    label: "Blockquote",
    command: "formatBlock",
    value: "blockquote",
    iconSrc: "/assets/icon/quote.svg",
  },
  { id: "link", label: "Insert link", command: "createLink", iconSrc: "/assets/icon/link.svg" },
  {
    id: "list",
    label: "Bulleted list",
    command: "insertUnorderedList",
    iconSrc: "/assets/icon/line.svg",
  },
];

const HIGHLIGHT_COLORS = [
  { id: "hl-yellow", label: "Yellow highlight", color: "#FFEB3B" },
  { id: "hl-green", label: "Green highlight", color: "#A5D6A7" },
  { id: "hl-blue", label: "Blue highlight", color: "#90CAF9" },
  { id: "hl-pink", label: "Pink highlight", color: "#F48FB1" },
  { id: "hl-orange", label: "Orange highlight", color: "#FFCC80" },
];

const applyBackColor = (editorRef, color, onContentChange) => {
  editorRef.current?.focus();
  document.execCommand("backColor", false, color);
  onContentChange?.(editorRef.current?.innerHTML || "");
};

function NoteRichTextEditor({
  title,
  content,
  onTitleChange,
  onContentChange,
  hideTitle = false,
}) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    if (editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content || "<p><br></p>";
    }
  }, [content]);

  const applyCommand = (action) => {
    editorRef.current?.focus();

    if (action.command === "createLink") {
      const url = window.prompt("Enter link URL");
      if (!url) {
        return;
      }
      document.execCommand("createLink", false, url);
    } else if (action.value) {
      document.execCommand(action.command, false, action.value);
    } else {
      document.execCommand(action.command, false);
    }

    onContentChange?.(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    onContentChange?.(editorRef.current?.innerHTML || "");
  };

  return (
    <div className="my-notes-editor">
      {!hideTitle ? (
        <input
          className="my-notes-editor-title"
          type="text"
          value={title}
          onChange={(event) => onTitleChange?.(event.target.value)}
          placeholder="Note title"
          aria-label="Note title"
        />
      ) : null}

      <div className="my-notes-editor-toolbar" role="toolbar" aria-label="Formatting tools">
        {TOOLBAR_ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            className="my-notes-editor-tool"
            aria-label={action.label}
            title={action.label}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => applyCommand(action)}
          >
            <img
              src={action.iconSrc}
              alt=""
              aria-hidden="true"
              className="my-notes-editor-tool-icon"
            />
          </button>
        ))}
        <div className="my-notes-editor-toolbar-divider" />
        {HIGHLIGHT_COLORS.map((c) => (
          <button
            key={c.id}
            type="button"
            className="my-notes-editor-tool my-notes-editor-tool-hl"
            style={{ backgroundColor: c.color }}
            aria-label={c.label}
            title={c.label}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyBackColor(editorRef, c.color, onContentChange)}
          />
        ))}
        <button
          type="button"
          className="my-notes-editor-tool my-notes-editor-tool-hl my-notes-editor-tool-hl-remove"
          aria-label="Remove highlight"
          title="Remove highlight"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyBackColor(editorRef, "transparent", onContentChange)}
        >
          ×
        </button>
      </div>

      <div
        ref={editorRef}
        className="my-notes-editor-body"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Note content"
        onInput={handleInput}
      />
    </div>
  );
}

export default NoteRichTextEditor;
